import { supabase } from './supabaseClient';

// Email-first admin check (current model) with legacy admin_users fallback.
async function fetchAdminProfile({ id, email }) {
  if (!id && !email) return null;
  if (email) {
    const { data, error } = await supabase
      .from('admin_emails')
      .select('email')
      .eq('email', email)
      .maybeSingle();
    if (!error && data) return { email };
  }
  if (id) {
    const { data, error } = await supabase
      .from('admin_users')
      .select('user_id, email')
      .eq('user_id', id)
      .maybeSingle();
    if (!error && data) return data;
  }
  return null;
}

export const auth = {
  async me() {
    // Read local session first (no network) — avoids the transient
    // "Failed to fetch" that getUser() throws when the auth client boots
    // before the network stack is ready. Supabase refreshes the token
    // automatically and fires TOKEN_REFRESHED, which calls refreshUser
    // again with a server-validated user.
    const { data: sessionData } = await supabase.auth.getSession();
    let sessionUser = sessionData?.session?.user;
    if (!sessionUser) {
      const err = new Error('Not authenticated');
      err.status = 401;
      throw err;
    }
    // Les droits (pages autorisées, boîtes, périmètre) vivent dans app_metadata, figé dans le
    // jeton local jusqu'à son renouvellement (~1 h). On relit le profil côté serveur pour qu'un
    // droit ajouté soit visible au prochain chargement, sans déconnexion ; en cas d'échec réseau,
    // on garde le jeton local.
    try {
      const { data: frais } = await supabase.auth.getUser();
      if (frais?.user) sessionUser = frais.user;
    } catch { /* hors ligne ou démarrage : le jeton local suffit */ }
    const adminProfile = await fetchAdminProfile({
      id: sessionUser.id,
      email: sessionUser.email,
    });
    return {
      id: sessionUser.id,
      email: sessionUser.email,
      full_name:
        sessionUser.user_metadata?.full_name ||
        sessionUser.user_metadata?.name ||
        sessionUser.email,
      avatar_url: sessionUser.user_metadata?.avatar_url,
      role: adminProfile ? 'admin' : 'user',
      is_admin: !!adminProfile,
      // Optional per-user nav restriction. When set on a non-admin user,
      // the sidebar shows only paths in this array. Admins always see
      // everything regardless. null = default behavior (non-admins see
      // all !adminOnly items).
      permitted_pages: Array.isArray(sessionUser.app_metadata?.permitted_pages)
        ? sessionUser.app_metadata.permitted_pages
        : null,
      // Optional per-user company scope. When set (e.g. ['WEBFORCE']),
      // dataContext filters leads, companies and salespersons down to
      // this scope before anything else. The user simply does not see
      // any data from other companies. null/undefined = no scoping.
      permitted_companies: Array.isArray(sessionUser.app_metadata?.permitted_companies)
        ? sessionUser.app_metadata.permitted_companies
        : null,
      // Renouvellements : vue d'ouverture ('recouvrement' = impayés > 800 €)
      // et thème choisi ('licorne' | 'standard').
      renouv_vue: sessionUser.user_metadata?.renouv_vue || null,
      renouv_theme: sessionUser.user_metadata?.renouv_theme || null,
      renouv_tuto_vu: !!sessionUser.user_metadata?.renouv_tuto_vu,
    };
  },

  async signIn(email, password) {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    return data;
  },

  // Envoie le mail de réinitialisation. `redirectTo` pointe explicitement sur
  // /reset-password ; sans lui, Supabase retomberait sur le Site URL du projet.
  async requestPasswordReset(email) {
    const redirectTo =
      typeof window !== 'undefined' ? `${window.location.origin}/reset-password` : undefined;
    const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo });
    if (error) throw error;
  },

  // Nécessite une session active : celle ouverte par le lien de récupération,
  // ou celle d'un utilisateur déjà connecté qui change son mot de passe.
  async updatePassword(password) {
    const { error } = await supabase.auth.updateUser({ password });
    if (error) throw error;
  },

  async logout() {
    await supabase.auth.signOut();
  },

  async redirectToLogin(redirectTo) {
    const target =
      redirectTo || (typeof window !== 'undefined' ? window.location.origin : undefined);
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: target,
        queryParams: { access_type: 'offline', prompt: 'consent' },
      },
    });
    if (error) throw error;
  },

  onAuthStateChange(callback) {
    const { data } = supabase.auth.onAuthStateChange((_event, session) => {
      callback(session?.user || null);
    });
    return () => data?.subscription?.unsubscribe();
  },

  async getSession() {
    const { data } = await supabase.auth.getSession();
    return data?.session || null;
  },
};

export default auth;
